import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Moment } from 'moment';
import { map } from 'rxjs/operators';
import { isArray } from 'util';
import { environment } from '../../../../environments/environment';
import { MachineGroupScheduleTimeRangeBuilder } from '../../models/builders/machine-group-schedule-time-range.builder';
import { MachineGroupBuilder } from '../../models/builders/machine-group.builder';
import { MachineGroupScheduleTimeRange } from '../../models/calendar/machine-group-schedule-time-range';
import { mapCollection } from '../../utils/collection.utils';
import { getFirstMillisOfDate, getMomentWithFormat } from '../../utils/date.utils';

const timeFormat = 'hh:mm:ss';

const createRangeBody = (timeRange: MachineGroupScheduleTimeRange) => {
  const startTime = timeRange.startTime;
  const momentStartTime: Moment = getMomentWithFormat(startTime, timeFormat);
  const momentStartTimeDate = getFirstMillisOfDate(startTime);
  const endHour = timeRange.endTime;
  const momentEndTime: Moment = getMomentWithFormat(endHour, timeFormat);
  const momentEndTimeDate = getFirstMillisOfDate(endHour);
  const start = momentStartTime.valueOf() - momentStartTimeDate;
  const end = momentEndTime.valueOf() - momentEndTimeDate;
  return (
    {
      hash: timeRange.hash,
      reference: timeRange.reference,
      color: timeRange.color,
      startTime: start,
      endTime: end,
      defaultRange: timeRange.defaultRange ? 1 : 0,
    }
  );
};

const createHashBody = (daySchedules: any) => {
  return daySchedules.hash;
};

const createScheduleHashBody = (daySchedules: any) => {
  return (isArray(daySchedules))
    ? mapCollection(createHashBody, daySchedules)
    : [];
};

const createDayScheduleBody = (daysSchedules: any) => {
  return (
    {
      date: daysSchedules.date,
      timeRangeHashes: createScheduleHashBody(daysSchedules.daySchedules),
    }
  );
};

const saveDatesTimeRangesBody = (daysSchedules: any) => {
  return (isArray(daysSchedules))
    ? mapCollection(createDayScheduleBody, daysSchedules)
    : [];
};

const dateSchedulesBody = (daysSchedules: any[]) => ({
  datesTimeRanges: saveDatesTimeRangesBody(daysSchedules)
});

const saveMachineGroupScheduleTimeRangesBody = (timeRanges: MachineGroupScheduleTimeRange[]) => {
  return (isArray(timeRanges))
    ? mapCollection(createRangeBody, timeRanges)
    : [];
};

const scheduleBody = (schedule: MachineGroupScheduleTimeRange[]) => ({
  timeRanges: saveMachineGroupScheduleTimeRangesBody(schedule)
});

@Injectable()
export class MachineGroupScheduleApiService {
  private apiUrl: string;

  constructor(private httpClient: HttpClient) {
    this.apiUrl = `${environment.apiUrl}working-model-groups`;
  }

  createMachineGroupSchedule(schedule: MachineGroupScheduleTimeRange[]) {
    return this.httpClient
      .post(`${this.apiUrl}/time-range`,  scheduleBody(schedule)).pipe(map(MachineGroupScheduleTimeRangeBuilder.fromJson));
  }

  deleteMachineGroupSchedule(schedule: MachineGroupScheduleTimeRange[]) {
    schedule.forEach(ranges => {
      return this.httpClient.delete(`${this.apiUrl}/${ranges.hash}`);
    });
  }

  getMachineGroupSchedules() {
    return this.httpClient.get(`${this.apiUrl}`).pipe(map(MachineGroupBuilder.fromList));
  }

  updateMachineGroupSchedule(hash: string, schedule: MachineGroupScheduleTimeRange[]) {
    return this.httpClient.put(`${this.apiUrl}/${hash}/time-ranges`,
      scheduleBody(schedule)
    );
  }

  getMachineGroupSchedule(hash: string) {
    return this.httpClient.get(`${this.apiUrl}/${hash}`)
      .pipe(
        map(MachineGroupBuilder.fromJson)
      );
  }

  getMachineGroupScheduleTimeRanges(hash: string) {
    return this.httpClient.get(`${this.apiUrl}/${hash}`)
      .pipe(
        map(MachineGroupScheduleTimeRangeBuilder.fromList)
      );
  }

  getMachineGroupDateSchedule(modelGroup: string, day: string) {
    return this.httpClient.get(`${this.apiUrl}/${modelGroup}/calendar-days/${day}/time-ranges`)
    .pipe(map(MachineGroupScheduleTimeRangeBuilder.fromList));
  }

  updateMachineGroupDateSchedule(daysSchedules: any[], modelGroup: string) {
    return this.httpClient
      .put(`${this.apiUrl}/${modelGroup}/calendar-days/time-ranges`,  dateSchedulesBody(daysSchedules));
  }

  updateMachineGroupYearSchedule(modelGroup: string) {
    return this.httpClient.put(`${this.apiUrl}/${modelGroup}/calendar-days/year/time-range`, {});
  }
}



// WEBPACK FOOTER //
// /var/www/borneo/prod/borneo_app/src/app/core/services/api/machine-group-schedule-api.service.ts